/**
 * The site a visitor audited with a free scan tool, carried into onboarding (experiment A, 2026-11-01).
 *
 * The free-tool review page's account doors (packages/server/src/review-app/components/FreeToolSessionChrome.tsx
 * and the header link, services/review-page/markup.ts buildTeamSignInLink) add `site=<review page URL>`. /sign-up
 * redirects to /sign-in keeping the query; SignInPage stashes the site here on mount, because the post-auth
 * navigate() and Google's full-page OAuth redirect both drop the query string (the same reason lib/freeScanClaim.ts
 * stashes its claim). A fresh onboarding wizard then starts with it as the website (OnboardingWizard
 * loadSavedState), so the visitor is not asked for it again. Nothing is claimed: scan results stay transient.
 *
 * The param is untrusted, so it passes the checks a typed onboarding URL passes (normalizeOnboardingWebsiteUrl and
 * onboardingWebsiteError) plus http(s) only and a length cap, on capture AND on read. Anything else is ignored
 * silently: the wizard simply starts empty, as it did before.
 */
import { normalizeOnboardingWebsiteUrl } from '@overlayqa/shared';
import { log } from '@/lib/logger';
import { onboardingWebsiteError } from '@/lib/onboardingWebsite';

const SITE_KEY = 'oqa_free_tool_site';
const STAMP_KEY = 'oqa_free_tool_site_ts';

/** Same window as the claim stash: bounds a shared tab handing one visitor's site to the next signup. */
export const FREE_TOOL_SITE_TTL_MS = 60 * 60 * 1000;
const SITE_MAX = 2048;

export function freeToolSiteFromParam(raw: string | null): string | null {
  const trimmed = raw?.trim();
  if (!trimmed || trimmed.length > SITE_MAX || !/^https?:\/\//i.test(trimmed)) return null;
  const normalized = normalizeOnboardingWebsiteUrl(trimmed);
  return normalized && !onboardingWebsiteError(normalized) ? normalized : null;
}

/** Called at /sign-in mount. A link without a valid site leaves any earlier stash alone. Returns what it stashed. */
export function captureFreeToolSite(search: string, now: number = Date.now()): string | null {
  let site: string | null;
  try {
    site = freeToolSiteFromParam(new URLSearchParams(search).get('site'));
  } catch {
    return null;
  }
  if (!site) return null;
  try {
    sessionStorage.setItem(SITE_KEY, site);
    sessionStorage.setItem(STAMP_KEY, String(now));
    log.info('[freeToolSite] captured audited site', { site });
  } catch {
    return null; // Storage blocked (private mode): onboarding asks for the URL, as before.
  }
  return site;
}

export function readFreeToolSite(now: number = Date.now()): string | null {
  try {
    const site = freeToolSiteFromParam(sessionStorage.getItem(SITE_KEY));
    const stamp = Number(sessionStorage.getItem(STAMP_KEY));
    if (site && Number.isFinite(stamp) && now - stamp < FREE_TOOL_SITE_TTL_MS) return site;
    clearFreeToolSite();
  } catch {
    // Storage blocked: nothing stashed.
  }
  return null;
}

export function clearFreeToolSite(): void {
  try {
    sessionStorage.removeItem(SITE_KEY);
    sessionStorage.removeItem(STAMP_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
